import { useAtomValue, useSetAtom } from 'jotai';

import { ActionItemStatus } from '@ekhodealer/ekho-common';
import { Button, SkeletonRect,Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { InfoLineItem } from '@common/components/InfoLineItem';

import { useGetShipBuyerPacketToSeller } from '../../../api/progress/useGetShipBuyerPacketToSeller';
import { useShipBuyerPacketToSeller } from '../../../api/progress/useShipBuyerPacketToSeller';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

import { downloadPdf } from './utils/downloadPdf';

interface ShipBuyerPacketToSellerDetailProps {
	actionItem: ActionItem;
}

/**
 * ShipBuyerPacketToSellerDetail component for shipping buyer-signed paperwork to seller for counter-signing.
 * Displays seller information and generates a UPS shipping label on submit.
 */
export const ShipBuyerPacketToSellerDetail = ({
	actionItem,
}: ShipBuyerPacketToSellerDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const shipBuyerPacketMutation = useShipBuyerPacketToSeller(orderId);
	const { data: sellerData, isLoading: isLoadingSellerData } =
		useGetShipBuyerPacketToSeller(orderId);

	const handleSubmit = async () => {
		try {
			const result = await shipBuyerPacketMutation.mutateAsync();

			// Download the shipping label PDF
			if (result.shippingLabelPdf) {
				const filename = `seller-packet-shipping-label-${orderId}.pdf`;
				downloadPdf(result.shippingLabelPdf, filename);
			}

			toast({
				message: `Shipping label generated successfully. Tracking: ${result.trackingNumber}`,
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to generate shipping label';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	};

	const isFormDisabled =
		shipBuyerPacketMutation.isPending || actionItem.status === ActionItemStatus.COMPLETE;

	const formatAddress = (address: {
		street: string;
		city: string;
		state: string;
		zipCode: string;
	}) => {
		return `${address.street}, ${address.city}, ${address.state} ${address.zipCode}`;
	};

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					Generate a UPS shipping label to send the buyer-signed paperwork packet to the
					seller for physical counter-signing. The label will automatically download when
					generated.
				</Text>
			</Stack>

			{/* Display seller info */}
			{isLoadingSellerData ? (
				<Stack itemsSpacing='8'>
					<SkeletonRect width='60%' height={20} />
					<SkeletonRect width='80%' height={20} />
					<SkeletonRect width='70%' height={20} />
				</Stack>
			) : (
				sellerData && (
					<Stack itemsSpacing='12'>
						<InfoLineItem title='Seller name' value={sellerData.dealerName} />
						<InfoLineItem
							title='Seller address'
							value={formatAddress(sellerData.dealerAddress)}
						/>
						<InfoLineItem
							title='Seller ID'
							value={sellerData.dealerServicesProviderId}
							isCopyable
						/>

						{sellerData.hasBeenShipped && sellerData.trackingNumber && (
							<>
								<InfoLineItem title='Status' value='Already shipped' />
								<InfoLineItem
									title='Tracking number'
									value={sellerData.trackingNumber}
									isCopyable
								/>
							</>
						)}
					</Stack>
				)
			)}

			<Stack itemsSpacing='16'>
				{actionItem.status !== ActionItemStatus.COMPLETE && (
					<Button
						size='large'
						isFullWidth
						onPress={handleSubmit}
						isLoading={shipBuyerPacketMutation.isPending}
						isDisabled={isLoadingSellerData || isFormDisabled}>
						{'Generate Shipping Label'}
					</Button>
				)}
			</Stack>
		</Stack>
	);
};
